import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Lock, Mail, Paperclip, Pencil, Trash2, X } from 'lucide-react'
import {
  ETICHETA_STARE_TASK,
  departamentulRolului,
  numeDepartament,
  type ActiuneTask,
  type DepartamentSlug,
  type StareTask,
  esteNotaArhivare,
  faraMarcajArhivare,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { useCitesteNotificarileTaskului } from '../../lib/notificari'
import { useAuthStore } from '../../stores/auth'
import { Button } from '../../components/ui/Button'
import { StareLista } from '../../components/ui/StareLista'
import { useConfirmare } from '../../components/ui/Confirmare'
import { EmailViewerModal } from '../../components/EmailViewerModal'
import { Avatar } from './CardFata'
import { FEREASTRA_GATA, MESAJ_ARHIVARE, MESAJ_STERGERE, PASI, coloanaPeTablou } from './constante'
import {
  CHEI,
  aziIso,
  depasit,
  mesajEroare,
  numeOameni,
  oraScurt,
  useActiune,
  useEditeaza,
  useReimprospateaza,
  useUrmarescPrivatele,
  zileDeAzi,
  ziScurt,
} from './date'
import { FormularTask, ModalTrimite } from './Formulare'
import { AlegeOameni, useOameni } from './AlegeOameni'
import { ModalMaiDeparte } from './MaiDeparte'

/**
 * Panoul lateral al unui task (docs/design.md: detaliile se deschid in dreapta).
 *
 * Sus, fara scroll: departamentul, titlul, unde e pe drum (pasii) si butoanele care chiar merg
 * pentru omul care se uita — le da serverul (`actiuni`). Cine nu poate muta vede de ce, si ca
 * poate scrie in discutie. Dedesubt: detaliile, documentele, discutia, traseul.
 */

interface TaskDetaliu {
  id: number
  title: string
  description: string | null
  status: StareTask
  urgent: boolean
  dueDate: string | null
  followUpDate: string | null
  waitingFor: string | null
  completionNote: string | null
  clientLabel: string | null
  client: { id: number; name: string } | null
  /** Oamenii pe task (19.10.2026: pot fi mai multi; oricare il muta si il inchide). */
  responsabili: {
    user: { id: number; name: string; active?: boolean; deletedAt?: string | null }
  }[]
  createdBy: { id: number; name: string }
  completedBy: { id: number; name: string } | null
  /** Privat: il vad responsabilii, `privatDe` (cine l-a dat) si cine urmareste privatele. */
  privat: boolean
  privatDe: { id: number; name: string } | null
  /** Privat venit din email (contact@): unde l-ar trimite filtrul. */
  departamentPropus: DepartamentSlug | null
  createdAt: string
  closedAt: string | null
  category: { slug: DepartamentSlug; name: string } | null
  sursaCategory: { slug: DepartamentSlug; name: string } | null
  blocatDeTask: {
    id: number
    title: string
    status: StareTask
    category: { slug: DepartamentSlug } | null
    /** Cererea face parte dintr-un grup (mai multe departamente): revine cand termina toate. */
    grupAtribuire: string | null
  } | null
  emailSubject: string | null
  emailFrom: string | null
  emailLink: string | null
  b4lEmailId: string | null
  source: string
  alertKey: string | null
  refType: string | null
}

interface ElementIstoric {
  kind: 'comment' | 'event'
  at: string
  data: {
    id: number
    body?: string
    author?: { id: number; name: string }
    actor?: { id: number; name: string } | null
    field?: string
    oldValue?: string | null
    newValue?: string | null
    note?: string | null
  }
}

interface Atasament {
  id: number
  filename: string
  size: number | null
  /** Venit din mail (28.10.2026): cand a sosit mailul. */
  dinEmailLa: string | null
}

/** Fisier din mail lasat deoparte — se spune, ca sa nu se piarda fara urma. */
interface Nepreluat {
  filename: string
  size: number | null
  motiv: 'prea_mare' | 'program'
}

const MOTIV_NEPRELUAT: Record<Nepreluat['motiv'], string> = {
  prea_mare: 'peste 20 MB',
  program: 'fișier de tip program, nu se preia',
}

/** De unde vine task-ul, in cuvinte. Fara asta, un task venit din email parea aparut din senin. */
function deUndeVine(t: TaskDetaliu): string {
  const cerere = t.sursaCategory && t.sursaCategory.slug !== t.category?.slug
  if (cerere) return `cerere de la ${numeDepartament(t.sursaCategory!.slug)} (${t.createdBy.name})`
  // Convertit de cineva din Inbox: e tot un email, doar ca l-a ales un om, nu clasificatorul.
  if (t.refType === 'inbox_message')
    return `din Inbox, de ${t.createdBy.name}${t.emailFrom ? ` — email de la ${t.emailFrom}` : ''}`
  switch (t.source) {
    case 'email_auto':
    case 'email':
    case 'imap':
      return t.emailFrom ? `din email, de la ${t.emailFrom}` : 'din email'
    case 'system':
      return t.alertKey ? 'dintr-o alertă' : 'automat (șablon recurent)'
    default:
      return `adăugat de ${t.createdBy.name}`
  }
}

const marime = (o: number | null) =>
  !o
    ? ''
    : o < 1024
      ? `${o} B`
      : o < 1048576
        ? `${Math.round(o / 1024)} KB`
        : `${(o / 1048576).toFixed(1)} MB`

export function PanouTask({ taskId, laInchidere }: { taskId: number; laInchidere: () => void }) {
  const eu = useAuthStore((s) => s.user)
  const { cere, confirma } = useConfirmare()
  const actiune = useActiune()
  const editeaza = useEditeaza(taskId)
  const reimprospateaza = useReimprospateaza()
  const qc = useQueryClient()
  const [fereastra, setFereastra] = useState<null | 'trimite' | 'cere' | 'email' | 'mai-departe'>(
    null,
  )
  // „Trimite mai departe" deschis direct pe departamentul propus de filtru (contact@).
  const [spreDepartament, setSpreDepartament] = useState<DepartamentSlug | null>(null)
  const [editez, setEditez] = useState<null | 'titlu' | 'descriere' | 'termen'>(null)
  const [daCuiva, setDaCuiva] = useState(false)
  const [text, setText] = useState('')

  const q = useQuery<{
    task: TaskDetaliu
    actiuni: ActiuneTask[]
    poateEdita: boolean
    poateOameni: boolean
    poateScoateAltii: boolean
    poateTrimite: boolean
    grup: MembruGrup[]
  }>({
    queryKey: CHEI.task(taskId),
    queryFn: async () => (await api.get(`/tasks/${taskId}`)).data,
  })
  const istoric = useQuery<{ timeline: ElementIstoric[] }>({
    queryKey: CHEI.timeline(taskId),
    queryFn: async () => (await api.get(`/tasks/${taskId}/timeline`)).data,
  })
  const atasamente = useQuery<{ atasamente: Atasament[]; nepreluate?: Nepreluat[] }>({
    queryKey: CHEI.atasamente(taskId),
    queryFn: async () => (await api.get(`/tasks/${taskId}/atasamente`)).data,
  })

  // Deschis = vazut: notificarile lui se marcheaza citite, si cand ai ajuns aici din tablou sau
  // din email, nu din clopotel. Doar dupa ce s-a incarcat — un task pe care nu-l vezi nu atinge
  // nimic. O data pe task, nu la fiecare reimprospatare.
  const citesteNotificarile = useCitesteNotificarileTaskului()
  const incarcat = q.isSuccess
  const marcat = useRef<number | null>(null)
  useEffect(() => {
    if (!incarcat || marcat.current === taskId) return
    marcat.current = taskId
    citesteNotificarile.mutate(taskId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate e stabil; vrem o data pe task
  }, [incarcat, taskId])

  // Escape inchide panoul — dar nu cand e deschisa o fereastra peste el (aceea se inchide prima).
  useEffect(() => {
    const tasta = (e: KeyboardEvent) => {
      // O fereastra deschisa peste panou (confirmare, „Pe cine aștepți?") se inchide prima.
      if (e.key === 'Escape' && !fereastra && !editez && !document.querySelector('[data-modal]'))
        laInchidere()
    }
    document.addEventListener('keydown', tasta)
    return () => document.removeEventListener('keydown', tasta)
  }, [fereastra, editez, laInchidere])

  const incarca = useMutation({
    mutationFn: async (fisiere: File[]) => {
      // Unul dupa altul: zece deodata de pe telefon se termina cu jumatate picate.
      for (const f of fisiere) {
        const fd = new FormData()
        fd.append('file', f)
        await api.post(`/tasks/${taskId}/atasamente`, fd, { timeout: 120_000 })
      }
      return fisiere.length
    },
    onSuccess: (n) => toast.success(n === 1 ? 'Document atașat' : `${n} documente atașate`),
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-au putut încărca toate documentele.')),
    onSettled: reimprospateaza,
  })

  const stergeDoc = useMutation({
    mutationFn: async (id: number) => api.delete(`/tasks/atasamente/${id}`),
    onSuccess: reimprospateaza,
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-a putut șterge documentul.')),
  })

  /** Doar adminul: pentru ce n-ar fi trebuit sa ajunga task (un raport, o notificare). Respinsul
   *  il lasa in Arhiva, la vedere — stergerea il scoate de tot. */
  const stergeTask = useMutation({
    mutationFn: async () => api.delete(`/tasks/${taskId}`),
    onSuccess: () => {
      toast.success('Task șters')
      laInchidere()
      // Fisa lui nu se mai cere: altfel reimprospatarea ar lovi un task care nu mai exista.
      qc.removeQueries({ queryKey: CHEI.task(taskId) })
      reimprospateaza()
    },
    onError: (e) => toast.error(mesajEroare(e, 'Nu s-a putut șterge task-ul.')),
  })
  const sterge = async () => {
    const da = await confirma({
      titlu: 'Ștergi task-ul definitiv?',
      mesaj: MESAJ_STERGERE,
      buton: 'Șterge definitiv',
      pericol: true,
    })
    if (da) stergeTask.mutate()
  }

  /** Descarcarea trece prin axios: autentificarea e un token in antet, nu un cookie. */
  const descarca = async (id: number, nume: string) => {
    try {
      const r = await api.get(`/tasks/atasamente/${id}`, { responseType: 'blob', timeout: 120_000 })
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nume
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Nu s-a putut descărca documentul.')
    }
  }

  const t = q.data?.task
  const actiuni = q.data?.actiuni ?? []
  const poateEdita = q.data?.poateEdita ?? false
  const poate = (a: ActiuneTask) => actiuni.includes(a)
  const fa = (a: ActiuneTask, extra: { motiv?: string | null } = {}) =>
    actiune.mutate({ id: taskId, actiune: a, ...extra })

  const gata = async () => {
    const nota = await cere(FEREASTRA_GATA)
    if (nota === null) return
    fa('gata', { motiv: nota || null })
  }
  /*
    Ești pe task și mai sunt și alții: ieși doar tu, task-ul rămâne la ei, în aceeași coloană —
    „Ies de pe task”, la fel ca pe un privat (Madalin, 29.10.2026). Altfel (singurul, sau un admin
    care nu e pe el): „Renunță”, înapoi la „De preluat”.
  */
  const peTask = (q.data?.task.responsabili ?? []).map((r) => r.user)
  // Un cont dezactivat ramas pe task nu „il tine": serverul il scoate odata cu tine.
  const altii = peTask.filter((o) => o.id !== eu?.id && o.active !== false && !o.deletedAt)
  const ies = peTask.some((o) => o.id === eu?.id) && altii.length > 0
  const renunta = async () => {
    const motiv = await cere({
      titlu: ies ? 'Ieși de pe task?' : 'Renunți la task?',
      mesaj: ies
        ? `Rămâne la ${numeOameni(altii)}. Poți spune de ce.`
        : 'Se întoarce la „De preluat”, pentru un coleg. Poți spune de ce.',
      eticheta: 'Motiv (opțional)',
      buton: ies ? 'Ies de pe task' : 'Renunță',
    })
    if (motiv === null) return
    fa('renunta', { motiv: motiv || null })
  }
  /*
    Task privat: ieși de pe el. Cu alți oameni rămași, rămâne la ei; ultimul de pe el îl dă înapoi
    celui care l-a dat (19.10.2026).
  */
  const ramaiCuAltii = ies
  const daInapoi = async () => {
    const lui = q.data?.task.privatDe?.name ?? 'cel care ți l-a dat'
    const motiv = await cere({
      titlu: ramaiCuAltii ? 'Ieși de pe task?' : `Îl dai înapoi lui ${lui}?`,
      mesaj: ramaiCuAltii
        ? 'Rămâne privat, la ceilalți de pe el. Poți spune de ce.'
        : 'Rămâne privat și trece la el, în lucru. Poți spune de ce.',
      eticheta: 'Motiv (opțional)',
      buton: ramaiCuAltii ? 'Ies de pe task' : 'Dă-l înapoi',
    })
    if (motiv === null) return
    fa('da_inapoi', { motiv: motiv || null })
  }
  /** Arhivare (16.10.2026): nu se mai face, dar rămâne în istoric — spre deosebire de ștergere. */
  const arhiveaza = async () => {
    const motiv = await cere({
      titlu: 'Arhivezi task-ul?',
      mesaj: MESAJ_ARHIVARE,
      eticheta: 'Motiv (opțional)',
      buton: 'Arhivează',
    })
    if (motiv === null) return
    fa('arhiveaza', { motiv: motiv || null })
  }
  const respinge = async () => {
    const motiv = await cere({
      titlu: 'Respingi task-ul?',
      mesaj:
        'Nu se face. Rămâne în Arhivă, la „Respinse”, cu motivul. Dacă era o cerere, departamentul care a cerut află.',
      eticheta: 'De ce',
      buton: 'Respinge',
      obligatoriu: true,
    })
    if (!motiv) return
    fa('respinge', { motiv })
  }

  const salveaza = (camp: 'titlu' | 'descriere' | 'termen') => {
    const v = text.trim()
    setEditez(null)
    if (!t) return
    if (camp === 'titlu' && v && v !== t.title) editeaza.mutate({ titlu: v })
    if (camp === 'descriere' && v !== (t.description ?? ''))
      editeaza.mutate({ descriere: v || null })
    if (camp === 'termen' && (v || null) !== (t.dueDate?.slice(0, 10) ?? null))
      editeaza.mutate({ termen: v || null })
  }

  const stareTablou = !t || t.status === 'cancelled' ? null : coloanaPeTablou(t.status)
  const idx = stareTablou ? PASI.indexOf(stareTablou) : -1
  const oameni = t?.responsabili.map((r) => r.user) ?? []
  const pePanou = oameni.some((o) => o.id === eu?.id)
  const cine = numeOameni(oameni)
  const alAltcuiva =
    !!t &&
    actiuni.length === 0 &&
    oameni.length > 0 &&
    !pePanou &&
    !['done', 'cancelled'].includes(t.status)
  const caAdmin =
    eu?.role === 'admin' && !t?.privat && oameni.length > 0 && !pePanou && actiuni.length > 0
  const laAltDepartament =
    !!t && actiuni.length === 0 && oameni.length === 0 && t.status === 'de_preluat'
  const poateOameni = q.data?.poateOameni ?? false
  const urmaresc = useUrmarescPrivatele()
  // Cine vede privatul: oamenii de pe el si cine l-a dat, o singura data fiecare. Cine urmareste
  // privatele se adauga doar daca nu e deja printre ei.
  const cineIlVede = t
    ? [...oameni, ...(t.privatDe ? [t.privatDe] : [])].filter(
        (o, i, l) => l.findIndex((x) => x.id === o.id) === i,
      )
    : []
  const urmarescAltii = urmaresc.numeFara(cineIlVede.map((o) => o.id))
  const poateTrimite = q.data?.poateTrimite ?? false

  const comentarii = (istoric.data?.timeline ?? []).filter((x) => x.kind === 'comment')
  const evenimente = (istoric.data?.timeline ?? []).filter(
    (x) =>
      x.kind === 'event' &&
      [
        'status',
        'departament',
        'assignee',
        'asteapta',
        'dueDate',
        'urgent',
        'title',
        'mail',
        'documente_mail',
      ].includes(x.data.field ?? ''),
  )
  /** Închis cu „Arhivează”, nu respins: aceeași stare, nota din traseu îi desparte. */
  const arhivat =
    t?.status === 'cancelled' &&
    evenimente.some((e) => e.data.newValue === 'cancelled' && esteNotaArhivare(e.data.note))

  return (
    <>
      {/* Sub ferestre (z-50): o confirmare deschisa din panou trebuie sa stea deasupra lui. */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={laInchidere} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t?.title ?? 'Task'}
        className="fixed inset-0 z-[45] flex flex-col border-b4l-border bg-b4l-surface-ridicata shadow-panou sm:inset-y-0 sm:left-auto sm:right-0 sm:w-full sm:max-w-[500px] sm:border-l"
      >
        <div className="grid gap-2.5 border-b border-b4l-border px-4 pb-3 pt-4">
          <div className="flex items-start justify-between gap-2.5">
            <div className="grid min-w-0 gap-1.5">
              <span className="flex flex-wrap gap-1.5">
                {t?.category && (
                  <span className="rounded-full bg-b4l-bg px-2 py-px text-eticheta font-semibold text-b4l-text">
                    {numeDepartament(t.category.slug)}
                  </span>
                )}
                {t?.urgent && t.status !== 'done' && (
                  <span className="rounded-full bg-b4l-red-light px-2 py-px text-eticheta font-semibold text-b4l-red-dark">
                    urgent
                  </span>
                )}
                {t?.status === 'cancelled' && (
                  <span className="rounded-full bg-b4l-bg px-2 py-px text-eticheta font-semibold text-b4l-text-muted">
                    {arhivat ? 'arhivat' : 'respins'}
                  </span>
                )}
                {t?.privat && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-b4l-purple-light px-2 py-px text-eticheta font-semibold text-b4l-purple-dark">
                    <Lock size={11} aria-hidden="true" /> privat
                  </span>
                )}
              </span>
              {editez === 'titlu' ? (
                <textarea
                  autoFocus
                  rows={2}
                  maxLength={200}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onBlur={() => salveaza('titlu')}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditez(null)
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      salveaza('titlu')
                    }
                  }}
                  className="w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1 text-sectiune font-semibold text-b4l-text"
                />
              ) : (
                <h2 className="text-sectiune font-semibold text-b4l-text [overflow-wrap:anywhere] [text-wrap:balance]">
                  {t?.title ?? 'Se încarcă…'}
                  {poateEdita && (
                    <button
                      type="button"
                      onClick={() => {
                        setText(t!.title)
                        setEditez('titlu')
                      }}
                      className="ml-1.5 inline-flex rounded p-0.5 align-middle text-b4l-text-dim hover:bg-b4l-bg hover:text-b4l-text"
                      aria-label="Rescrie titlul"
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                </h2>
              )}
            </div>
            <span className="flex shrink-0 items-center gap-0.5">
              {eu?.role === 'admin' && t && (
                <button
                  type="button"
                  onClick={sterge}
                  disabled={stergeTask.isPending}
                  className="rounded-md p-1 text-b4l-text-dim hover:bg-b4l-red-light hover:text-b4l-red-dark disabled:opacity-50"
                  aria-label="Șterge task-ul definitiv"
                  title="Șterge definitiv (doar admin)"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button
                type="button"
                onClick={laInchidere}
                className="rounded-md p-1 text-b4l-text-dim hover:bg-b4l-bg hover:text-b4l-text"
                aria-label="Închide"
              >
                <X size={18} />
              </button>
            </span>
          </div>

          {t && t.status !== 'cancelled' && (
            <ol className="flex gap-1" aria-label="Unde e task-ul">
              {PASI.map((p, i) => (
                <li
                  key={p}
                  aria-current={i === idx ? 'step' : undefined}
                  className={`flex-1 rounded-md px-0.5 py-1 text-center text-eticheta font-semibold ${
                    i === idx
                      ? 'bg-b4l-blue text-b4l-pe-accent'
                      : i < idx
                        ? 'bg-b4l-green-light text-b4l-green-dark'
                        : 'bg-b4l-bg text-b4l-text-dim'
                  }`}
                >
                  {ETICHETA_STARE_TASK[p]}
                </li>
              ))}
            </ol>
          )}

          {t && (
            <div className="flex flex-wrap gap-1.5">
              {poate('preia') && (
                <Button loading={actiune.isPending} onClick={() => fa('preia')}>
                  Preia
                </Button>
              )}
              {poate('reia') && (
                <Button loading={actiune.isPending} onClick={() => fa('reia')}>
                  Înapoi în lucru
                </Button>
              )}
              {poate('gata') && (
                <Button
                  loading={actiune.isPending}
                  onClick={gata}
                  className="!bg-b4l-green hover:brightness-110"
                >
                  Gata
                </Button>
              )}
              {/* Dintr-un clic, fara „pe cine aștepți" (Madalin, 19.10.2026). */}
              {poate('asteapta') && t.status !== 'de_preluat' && (
                <Button
                  variant="secondary"
                  loading={actiune.isPending}
                  onClick={() => fa('asteapta')}
                >
                  Pune în așteptare
                </Button>
              )}
              {/* Dintr-un task privat nu se cere altui departament: cererea ar ieși la vedere. */}
              {poate('asteapta') && !t.privat && ['todo', 'in_progress'].includes(t.status) && (
                <Button variant="secondary" onClick={() => setFereastra('cere')}>
                  Cer altui departament
                </Button>
              )}
              {poateTrimite && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSpreDepartament(null)
                    setFereastra('mai-departe')
                  }}
                >
                  Trimite mai departe…
                </Button>
              )}
              {poate('da_inapoi') && (ramaiCuAltii || (t.privatDe && t.privatDe.id !== eu?.id)) && (
                <Button variant="secondary" onClick={daInapoi}>
                  {ramaiCuAltii
                    ? 'Ies de pe task'
                    : `Dă-l înapoi lui ${t.privatDe?.name ?? 'cel care ți l-a dat'}`}
                </Button>
              )}
              {poate('renunta') && (
                <Button variant="secondary" onClick={renunta}>
                  {ies ? 'Ies de pe task' : 'Renunță'}
                </Button>
              )}
              {poate('preia') && (
                <Button variant="secondary" onClick={() => setFereastra('trimite')}>
                  Nu e al nostru
                </Button>
              )}
              {poate('respinge') && (
                <Button variant="ghost" onClick={respinge}>
                  Respinge
                </Button>
              )}
              {poate('arhiveaza') && (
                <Button variant="ghost" onClick={arhiveaza}>
                  Arhivează
                </Button>
              )}
              {poate('redeschide') && (
                <Button
                  variant="secondary"
                  onClick={async () => {
                    if (
                      await confirma({
                        titlu: 'Redeschizi task-ul?',
                        mesaj: 'Revine în „În lucru”, la cine l-a terminat.',
                        buton: 'Redeschide',
                      })
                    )
                      fa('redeschide')
                  }}
                >
                  Redeschide
                </Button>
              )}
            </div>
          )}
          {alAltcuiva && (
            <p className="flex gap-2 rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text-muted">
              <Lock size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Preluat de <b className="text-b4l-text">{cine}</b>.{' '}
                {oameni.length > 1
                  ? 'Doar ei îl pot'
                  : `Doar ${oameni[0]!.name.split(' ')[0]} îl poate`}{' '}
                începe, pune în așteptare, termina sau lăsa înapoi. Poți scrie în discuție — cu @
                {oameni.length > 1 ? ' îi anunți.' : ' îl anunți.'}
              </span>
            </p>
          )}
          {laAltDepartament && (
            <p className="rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text-muted">
              E la {numeDepartament(t!.category?.slug ?? '')}, încă nepreluat. Poți scrie în
              discuție; stadiul îl mută ei.
            </p>
          )}
          {caAdmin && (
            <p className="rounded-caseta bg-b4l-amber-light px-3 py-2 text-mic text-b4l-amber-dark">
              Îl {oameni.length > 1 ? 'au' : 'are'} {cine}. Îl muți ca admin — mutarea rămâne scrisă
              în traseu, cu numele tău, iar {oameni.length > 1 ? 'ei primesc' : 'el primește'}{' '}
              notificare.
            </p>
          )}
        </div>

        {/*
          `grid-cols-[minmax(0,1fr)]`: fara ea, coloana implicita a grilei se lata dupa cel mai lung
          rand care nu se poate rupe — o adresa de email dintr-un mail redirectionat, un camp cu
          `truncate` — si tot panoul se derula pe orizontala, cu textul taiat la dreapta
          (Madalin, 27.10.2026).
        */}
        <div className="grid flex-1 grid-cols-[minmax(0,1fr)] content-start gap-5 overflow-y-auto overflow-x-hidden px-4 pb-6 pt-3.5">
          <StareLista interogare={q} gol={false} mesajGol="">
            {t && (
              <>
                <dl className="grid grid-cols-[110px_1fr] items-center gap-x-3 gap-y-2 text-corp max-sm:grid-cols-1">
                  <dt className="text-b4l-text-dim">
                    {t.status === 'done' ? 'Terminat de' : 'Responsabili'}
                  </dt>
                  <dd className="flex flex-wrap items-center gap-1.5">
                    {t.status === 'done' && t.completedBy ? (
                      <>
                        <Avatar nume={t.completedBy.name} />
                        {t.completedBy.name}
                        {oameni.length > 1 && (
                          <span className="text-mic text-b4l-text-dim">· împreună cu {cine}</span>
                        )}
                      </>
                    ) : oameni.length ? (
                      oameni.map((o) => (
                        <span
                          key={o.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-b4l-border bg-b4l-surface py-0.5 pl-0.5 pr-2 text-mic"
                        >
                          <Avatar nume={o.name} />
                          {o.name}
                          {o.id === eu?.id && <span className="text-b4l-text-dim">· tu</span>}
                        </span>
                      ))
                    ) : (
                      <span className="text-b4l-text-dim">încă de nimeni — e la „De preluat”</span>
                    )}
                    {poateOameni && (
                      <button
                        type="button"
                        onClick={() => setDaCuiva(true)}
                        className="rounded-md px-1.5 py-0.5 text-mic font-semibold text-b4l-blue hover:bg-b4l-bg"
                      >
                        {oameni.length ? 'Schimbă' : '+ Pune pe cineva'}
                      </button>
                    )}
                  </dd>
                  <dt className="text-b4l-text-dim">Termen</dt>
                  <dd className="flex flex-wrap items-center gap-1.5">
                    {editez === 'termen' ? (
                      <input
                        type="date"
                        autoFocus
                        defaultValue={t.dueDate?.slice(0, 10) ?? ''}
                        min={aziIso()}
                        onChange={(e) => setText(e.target.value)}
                        onBlur={() => salveaza('termen')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') salveaza('termen')
                          if (e.key === 'Escape') setEditez(null)
                        }}
                        className="rounded-caseta border border-b4l-border bg-b4l-surface px-2 py-1 text-corp"
                      />
                    ) : (
                      <>
                        {t.dueDate ? (
                          <span
                            className={
                              depasit({ termen: t.dueDate, stare: t.status })
                                ? 'font-semibold text-b4l-red-dark'
                                : zileDeAzi(t.dueDate) <= 1 && t.status !== 'done'
                                  ? 'font-semibold text-b4l-amber-dark'
                                  : 'text-b4l-text'
                            }
                          >
                            {ziScurt(t.dueDate)}
                            {depasit({ termen: t.dueDate, stare: t.status }) && ' · depășit'}
                          </span>
                        ) : (
                          <span className="text-b4l-text-dim">fără termen</span>
                        )}
                        {poateEdita && !['done', 'cancelled'].includes(t.status) && (
                          <button
                            type="button"
                            onClick={() => {
                              setText(t.dueDate?.slice(0, 10) ?? '')
                              setEditez('termen')
                            }}
                            className="text-mic text-b4l-blue hover:underline"
                          >
                            {t.dueDate ? 'schimbă' : 'adaugă'}
                          </button>
                        )}
                        {poateEdita && t.dueDate && !['done', 'cancelled'].includes(t.status) && (
                          <button
                            type="button"
                            onClick={() => editeaza.mutate({ termen: null })}
                            className="text-mic text-b4l-text-muted hover:underline"
                          >
                            scoate
                          </button>
                        )}
                      </>
                    )}
                  </dd>
                  {/*
                    Urgent se schimba si dupa creare (Madalin, 27.10.2026: „in caz ca ai nevoie sa
                    modifici termenul sau sa il pui ca si urgent"). Aceleasi drepturi ca termenul;
                    serverul verifica oricum (`poateEdita`) si scrie schimbarea in Traseu.
                  */}
                  <dt className="text-b4l-text-dim">Urgent</dt>
                  <dd className="flex flex-wrap items-center gap-1.5">
                    {t.urgent ? (
                      <span className="font-semibold text-b4l-red-dark">da</span>
                    ) : (
                      <span className="text-b4l-text-dim">nu</span>
                    )}
                    {poateEdita && !['done', 'cancelled'].includes(t.status) && (
                      <button
                        type="button"
                        disabled={editeaza.isPending}
                        onClick={() => editeaza.mutate({ urgent: !t.urgent })}
                        className="text-mic text-b4l-blue hover:underline disabled:opacity-50"
                      >
                        {t.urgent ? 'nu mai e urgent' : 'marchează urgent'}
                      </button>
                    )}
                  </dd>
                  <dt className="text-b4l-text-dim">A intrat</dt>
                  <dd>
                    {deUndeVine(t)} · {ziScurt(t.createdAt)}
                  </dd>
                  {t.status === 'in_asteptare' && (
                    <>
                      <dt className="text-b4l-text-dim">Așteaptă</dt>
                      <dd>
                        {t.blocatDeTask?.grupAtribuire ? (
                          <>
                            {t.waitingFor ?? '—'}: „{t.blocatDeTask.title}” ·{' '}
                            <span className="text-b4l-text-muted">
                              revine singur când au terminat toate
                            </span>
                          </>
                        ) : t.blocatDeTask ? (
                          <>
                            {numeDepartament(t.blocatDeTask.category?.slug ?? '')}: „
                            {t.blocatDeTask.title}” ·{' '}
                            <span className="text-b4l-text-muted">
                              {ETICHETA_STARE_TASK[t.blocatDeTask.status]} · revine singur când e
                              gata
                            </span>
                          </>
                        ) : (
                          <>
                            {t.waitingFor ?? '—'}
                            {t.followUpDate && (
                              <span className="text-b4l-text-muted">
                                {' '}
                                · revine singur pe {ziScurt(t.followUpDate)}
                              </span>
                            )}
                          </>
                        )}
                      </dd>
                    </>
                  )}
                  {(t.client?.name ?? t.clientLabel) && (
                    <>
                      <dt className="text-b4l-text-dim">Legat de</dt>
                      <dd>{t.client?.name ?? t.clientLabel}</dd>
                    </>
                  )}
                  {(t.emailFrom || t.b4lEmailId) && (
                    <>
                      <dt className="text-b4l-text-dim">Email</dt>
                      <dd className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="min-w-0 truncate">
                          {t.emailSubject ?? t.emailFrom ?? '—'}
                        </span>
                        {t.b4lEmailId && t.refType !== 'webmail' && (
                          <button
                            type="button"
                            onClick={() => setFereastra('email')}
                            className="inline-flex items-center gap-1 text-mic text-b4l-blue hover:underline"
                          >
                            <Mail size={12} aria-hidden="true" /> vezi emailul
                          </button>
                        )}
                        {t.emailLink && !t.b4lEmailId && (
                          <a
                            href={t.emailLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-mic text-b4l-blue hover:underline"
                          >
                            caută în webmail
                          </a>
                        )}
                      </dd>
                    </>
                  )}
                </dl>

                {(q.data?.grup.length ?? 0) > 1 && (
                  <GrupDepartamente
                    grup={q.data!.grup}
                    aici={t.id}
                    deCine={t.createdBy.name}
                    eAdmin={eu?.role === 'admin'}
                  />
                )}
                {t.departamentPropus && poateTrimite && (
                  <div className="grid gap-2 rounded-caseta bg-b4l-blue-light px-3 py-2.5 text-corp text-b4l-text">
                    <span className="text-eticheta font-semibold uppercase tracking-wide text-b4l-blue-dark">
                      Propunerea filtrului
                    </span>
                    <span>
                      Venit pe email. Filtrul l-ar da la{' '}
                      <b>{numeDepartament(t.departamentPropus)}</b>. Trimite-l acolo, la altcineva,
                      sau păstrează-l dacă e de tine.
                    </span>
                    <span className="flex flex-wrap gap-1.5">
                      <Button
                        onClick={() => {
                          setSpreDepartament(t.departamentPropus)
                          setFereastra('mai-departe')
                        }}
                      >
                        Trimite la {numeDepartament(t.departamentPropus)}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setSpreDepartament(null)
                          setFereastra('mai-departe')
                        }}
                      >
                        Altundeva…
                      </Button>
                    </span>
                  </div>
                )}
                {t.privat && (
                  <p className="flex gap-2 rounded-caseta bg-b4l-purple-light px-3 py-2 text-corp text-b4l-purple-dark">
                    <Lock size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0">
                      Privat: îl văd <b>doar {numeOameni(cineIlVede) || 'cei de pe el'}</b>
                      {urmarescAltii && (
                        <>
                          {' '}
                          și <b>{urmarescAltii}</b>, care urmărește privatele
                        </>
                      )}{' '}
                      — departamentele și ceilalți admini nu.
                      {t.privatDe ? ` Dat de ${t.privatDe.name}.` : ''}
                      {!pePanou && t.privatDe?.id !== eu?.id && urmaresc.ids.includes(eu?.id ?? -1)
                        ? ' Îl vezi pentru că urmărești privatele: îl poți comenta și edita, nu muta.'
                        : ''}
                    </span>
                  </p>
                )}
                {t.completionNote && (
                  <p
                    className={`rounded-caseta px-3 py-2 text-corp [overflow-wrap:anywhere] ${
                      t.status === 'cancelled'
                        ? 'bg-b4l-bg text-b4l-text'
                        : 'bg-b4l-green-light text-b4l-green-dark'
                    }`}
                  >
                    <b>
                      {t.status === 'cancelled' ? (arhivat ? 'Arhivat:' : 'Respins:') : 'La final:'}
                    </b>{' '}
                    {t.completionNote}
                  </p>
                )}

                <Sectiune titlu="Detalii">
                  {editez === 'descriere' ? (
                    <div className="grid gap-1.5">
                      <textarea
                        autoFocus
                        rows={5}
                        maxLength={5000}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Escape' && setEditez(null)}
                        placeholder="Ce ai aflat, ce a rămas de făcut, cu cine ai vorbit…"
                        className="w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text"
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => salveaza('descriere')} loading={editeaza.isPending}>
                          Salvează
                        </Button>
                        <Button variant="secondary" onClick={() => setEditez(null)}>
                          Renunță
                        </Button>
                      </div>
                    </div>
                  ) : t.description ? (
                    <p className="whitespace-pre-wrap text-corp text-b4l-text [overflow-wrap:anywhere]">
                      {t.description}
                      {poateEdita && (
                        <button
                          type="button"
                          onClick={() => {
                            setText(t.description ?? '')
                            setEditez('descriere')
                          }}
                          className="ml-1.5 inline-flex rounded p-0.5 align-middle text-b4l-text-dim hover:text-b4l-text"
                          aria-label="Rescrie detaliile"
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                    </p>
                  ) : poateEdita ? (
                    <button
                      type="button"
                      onClick={() => {
                        setText('')
                        setEditez('descriere')
                      }}
                      className="justify-self-start text-corp text-b4l-text-dim hover:text-b4l-blue"
                    >
                      + Adaugă detalii
                    </button>
                  ) : (
                    <p className="text-mic text-b4l-text-dim">Fără detalii.</p>
                  )}
                </Sectiune>

                <Sectiune
                  titlu={`Documente${atasamente.data?.atasamente.length ? ` · ${atasamente.data.atasamente.length}` : ''}`}
                >
                  <div className="grid grid-cols-[minmax(0,1fr)] gap-1.5">
                    {(atasamente.data?.atasamente ?? []).map((a) => (
                      <div key={a.id} className="flex items-center gap-2 text-corp">
                        <Paperclip
                          size={13}
                          className="shrink-0 text-b4l-text-dim"
                          aria-hidden="true"
                        />
                        <button
                          type="button"
                          onClick={() => descarca(a.id, a.filename)}
                          className="min-w-0 flex-1 truncate text-left text-b4l-blue hover:underline"
                        >
                          {a.filename}
                        </button>
                        <span className="shrink-0 text-mic tabular-nums text-b4l-text-dim">
                          {a.dinEmailLa
                            ? `din email, ${new Date(a.dinEmailLa).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })} · `
                            : ''}
                          {marime(a.size)}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            if (
                              await confirma({
                                titlu: 'Ștergi documentul?',
                                mesaj: a.filename,
                                buton: 'Șterge',
                                pericol: true,
                              })
                            )
                              stergeDoc.mutate(a.id)
                          }}
                          className="shrink-0 text-mic text-b4l-red hover:underline"
                        >
                          Șterge
                        </button>
                      </div>
                    ))}
                    {(atasamente.data?.nepreluate ?? []).length > 0 && (
                      <p className="rounded-caseta bg-b4l-amber-light px-3 py-2 text-mic text-b4l-amber-dark [overflow-wrap:anywhere]">
                        {(atasamente.data?.nepreluate ?? []).length === 1
                          ? 'Un fișier din mail n-a fost preluat: '
                          : 'Fișiere din mail nepreluate: '}
                        {(atasamente.data?.nepreluate ?? [])
                          .map((n) => `${n.filename} (${MOTIV_NEPRELUAT[n.motiv]})`)
                          .join(', ')}
                        . Sunt în continuare în mail.
                      </p>
                    )}
                    <label className="cursor-pointer justify-self-start text-corp text-b4l-blue hover:underline">
                      {incarca.isPending ? 'Se încarcă…' : '+ Atașează documente'}
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        disabled={incarca.isPending}
                        onChange={(e) => {
                          const f = Array.from(e.target.files ?? [])
                          e.target.value = ''
                          if (f.length) incarca.mutate(f)
                        }}
                      />
                    </label>
                  </div>
                </Sectiune>

                <Sectiune titlu={`Discuție${comentarii.length ? ` · ${comentarii.length}` : ''}`}>
                  <div className="grid gap-2">
                    {comentarii.map((c) => (
                      <div
                        key={c.data.id}
                        className="grid gap-0.5 rounded-caseta bg-b4l-bg-soft px-3 py-2"
                      >
                        <span className="text-mic text-b4l-text-dim">
                          {c.data.author?.name} · {ziScurt(c.at)}, {oraScurt(c.at)}
                        </span>
                        <span className="whitespace-pre-wrap text-corp text-b4l-text [overflow-wrap:anywhere]">
                          {c.data.body}
                        </span>
                      </div>
                    ))}
                    {comentarii.length === 0 && (
                      <p className="text-mic text-b4l-text-dim">Nicio discuție încă.</p>
                    )}
                    <ComentariuNou
                      taskId={taskId}
                      departament={t.category?.slug ?? null}
                      sursa={t.sursaCategory?.slug ?? null}
                      oameniPeTask={[
                        ...oameni.map((o) => o.id),
                        ...(t.privatDe ? [t.privatDe.id] : []),
                        // Cine urmareste privatele le vede, deci poate fi anuntat.
                        ...(t.privat ? urmaresc.ids : []),
                      ]}
                      privat={t.privat}
                      euId={eu?.id ?? null}
                      laTrimis={reimprospateaza}
                    />
                  </div>
                </Sectiune>

                <Sectiune titlu="Traseu">
                  <Traseu task={t} evenimente={evenimente} />
                </Sectiune>
              </>
            )}
          </StareLista>
        </div>
      </aside>

      {fereastra === 'trimite' && t && (
        <ModalTrimite
          task={{ id: t.id, titlu: t.title, departament: t.category?.slug ?? null }}
          laInchidere={() => setFereastra(null)}
          laTrimis={laInchidere}
        />
      )}
      {fereastra === 'cere' && t && (
        <FormularTask
          alMeu={null}
          departamentInitial={null}
          pentruTask={{ id: t.id, titlu: t.title, departament: t.category?.slug ?? null }}
          laInchidere={() => setFereastra(null)}
        />
      )}
      {fereastra === 'email' && t && (
        <EmailViewerModal
          taskId={t.id}
          b4lEmailId={t.b4lEmailId}
          emailLink={t.emailLink}
          onClose={() => setFereastra(null)}
        />
      )}
      {daCuiva && t && (
        <AlegeOameni
          task={{
            id: t.id,
            titlu: t.title,
            departament: t.category?.slug ?? null,
            privat: t.privat,
            responsabili: oameni,
            poateScoateAltii: q.data?.poateScoateAltii ?? false,
          }}
          laInchidere={() => setDaCuiva(false)}
        />
      )}
      {fereastra === 'mai-departe' && t && (
        <ModalMaiDeparte
          task={{ id: t.id, titlu: t.title }}
          departamentInitial={spreDepartament}
          laInchidere={() => setFereastra(null)}
          laTrimis={() => {
            setFereastra(null)
            reimprospateaza()
          }}
        />
      )}
    </>
  )
}

function Sectiune({ titlu, children }: { titlu: string; children: ReactNode }) {
  return (
    // Coloana fixata si aici: un nume de document lung impingea „Sterge” in afara panoului.
    <section className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2">
      <h3 className="text-eticheta font-semibold uppercase tracking-wide text-b4l-text-dim">
        {titlu}
      </h3>
      {children}
    </section>
  )
}

/** Fraza unei schimbari de stare, din jurnal. */
function fraza(
  din: string | null | undefined,
  spre: string | null | undefined,
  nota?: string | null,
): string {
  if (spre === 'de_preluat')
    return din ? 'l-a lăsat înapoi la „De preluat”' : 'a intrat la „De preluat”'
  if (spre === 'todo') return din === 'done' ? 'l-a redeschis' : 'l-a preluat'
  if (spre === 'in_progress')
    return din === 'in_asteptare'
      ? 'l-a reluat'
      : din === 'de_preluat'
        ? 'l-a preluat'
        : din === 'done'
          ? 'l-a redeschis'
          : 'a început'
  if (spre === 'in_asteptare') return 'l-a pus în așteptare'
  if (spre === 'done') return 'l-a terminat'
  if (spre === 'cancelled') return esteNotaArhivare(nota) ? 'l-a arhivat' : 'l-a respins'
  return `l-a mutat în ${ETICHETA_STARE_TASK[spre as StareTask] ?? spre}`
}

const BULINA: Record<string, string> = {
  de_preluat: 'bg-b4l-amber',
  todo: 'bg-b4l-text-dim',
  in_progress: 'bg-b4l-blue',
  in_asteptare: 'bg-b4l-purple',
  done: 'bg-b4l-green',
  cancelled: 'bg-b4l-text-dim',
}

interface MembruGrup {
  id: number
  departament: DepartamentSlug | null
  stare: StareTask
  responsabil: string | null
}

const PASTILA_STARE: Record<string, string> = {
  de_preluat: 'bg-b4l-amber-light text-b4l-amber-dark',
  todo: 'bg-b4l-amber-light text-b4l-amber-dark',
  in_progress: 'bg-b4l-blue-light text-b4l-blue-dark',
  in_asteptare: 'bg-b4l-purple-light text-b4l-purple-dark',
  done: 'bg-b4l-green-light text-b4l-green-dark',
  cancelled: 'bg-b4l-bg text-b4l-text-muted',
}

/**
 * Trimis deodata la mai multe departamente (16.10.2026): unde a mai ajuns si cum sta acolo —
 * doar departamentul, starea si cine il are; continutul ramane la fiecare. Adminul le poate
 * deschide; ceilalti nu le vad pe tablou, deci n-au link.
 */
function GrupDepartamente({
  grup,
  aici,
  deCine,
  eAdmin,
}: {
  grup: MembruGrup[]
  aici: number
  deCine: string
  eAdmin: boolean
}) {
  const inchise = grup.filter((g) => g.stare === 'done' || g.stare === 'cancelled').length
  // Deschide alt task din grup in acelasi panou, cu filtrele tabloului pastrate.
  const [param] = useSearchParams()
  const spre = (id: number) => {
    const p = new URLSearchParams(param)
    p.set('view', String(id))
    return `?${p.toString()}`
  }
  return (
    <section
      aria-label="Trimis la mai multe departamente"
      className="grid gap-2 rounded-caseta border border-b4l-border px-3 py-2.5"
    >
      <span className="text-mic font-semibold text-b4l-text-muted">
        Trimis deodată la {grup.length} departamente, de {deCine} · {inchise} din {grup.length}{' '}
        încheiate
      </span>
      <ul className="grid gap-1.5">
        {grup.map((g) => {
          const nume = numeDepartament(g.departament ?? '')
          return (
            <li
              key={g.id}
              className={`flex flex-wrap items-center justify-between gap-2 text-corp ${
                g.id === aici ? 'font-semibold text-b4l-text' : 'text-b4l-text'
              }`}
            >
              {eAdmin && g.id !== aici ? (
                <Link to={spre(g.id)} className="text-b4l-blue hover:underline">
                  {nume}
                </Link>
              ) : (
                <span>
                  {nume}
                  {g.id === aici && <span className="font-normal text-b4l-text-dim"> · aici</span>}
                </span>
              )}
              <span
                className={`rounded-full px-2 py-px text-eticheta font-semibold ${PASTILA_STARE[g.stare] ?? ''}`}
              >
                {ETICHETA_STARE_TASK[g.stare]}
                {g.responsabil && g.stare !== 'de_preluat' && ` · ${g.responsabil}`}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** Drumul task-ului, din jurnal: a intrat, preluat, inceput, asteptat, terminat — cine si cand. */
function Traseu({ task, evenimente }: { task: TaskDetaliu; evenimente: ElementIstoric[] }) {
  // Jurnalul tine id-uri de oameni; numele vin din lista colegilor, deja incarcata.
  const toti = useOameni()
  const numele = (id: string | null | undefined) =>
    toti.data?.users.find((u) => u.id === Number(id))?.name ?? 'un coleg'
  // Schimbarea de responsabil fara schimbare de stare (adminul muta, dezactivare) apare separat;
  // cea care insoteste o schimbare de stare e deja spusa de fraza starii.
  const pasi = evenimente.filter(
    (e, i) =>
      e.data.field !== 'assignee' ||
      !evenimente.some(
        (x, j) =>
          j !== i &&
          x.data.field === 'status' &&
          Math.abs(new Date(x.at).getTime() - new Date(e.at).getTime()) < 2000,
      ),
  )
  return (
    <ol className="grid">
      <Pas bulina="bg-b4l-amber" cand={task.createdAt}>
        <b>A intrat</b> {task.category ? `la ${numeDepartament(task.category.slug)}` : 'privat'} —{' '}
        {deUndeVine(task)}
      </Pas>
      {pasi.map((e) => (
        <Pas key={e.data.id} bulina={BULINA[e.data.newValue ?? ''] ?? 'bg-b4l-blue'} cand={e.at}>
          <b>{e.data.actor?.name ?? 'Automat'}</b>{' '}
          {e.data.field === 'status'
            ? fraza(e.data.oldValue, e.data.newValue, e.data.note)
            : e.data.field === 'asteapta'
              ? `(${numeDepartament(e.data.oldValue ?? '')}) ${e.data.note ?? 'a încheiat cererea'}; mai așteaptă ${e.data.newValue ?? ''}`
              : e.data.field === 'departament'
                ? `l-a trimis la ${numeDepartament(e.data.newValue ?? '')}`
                : e.data.field === 'privat'
                  ? `l-a scos la vedere${e.data.note ? ` — ${e.data.note}` : ''}`
                  : e.data.field === 'category'
                    ? `l-a dat la ${numeDepartament(e.data.newValue ?? '')}`
                    : e.data.field === 'documente_mail'
                      ? `a pus la Documente, din mail: ${e.data.newValue ?? ''}`
                      : e.data.field === 'mail'
                        ? `a venit și pe mail: „${e.data.newValue ?? 'fără subiect'}” — n-a mai făcut alt task`
                        : e.data.field === 'title'
                          ? `a schimbat titlul din „${e.data.oldValue ?? ''}”`
                          : e.data.field === 'dueDate'
                            ? e.data.newValue
                              ? e.data.oldValue
                                ? `a mutat termenul de pe ${ziScurt(e.data.oldValue)} pe ${ziScurt(e.data.newValue)}`
                                : `a pus termenul ${ziScurt(e.data.newValue)}`
                              : 'a scos termenul'
                            : e.data.field === 'urgent'
                              ? e.data.newValue === 'true'
                                ? 'l-a marcat urgent'
                                : 'a scos „urgent”'
                              : // Cate un eveniment pe om (19.10.2026): `null → X` a intrat, `X → null` a iesit.
                                e.data.newValue
                                ? Number(e.data.newValue) === e.data.actor?.id
                                  ? 's-a pus pe task'
                                  : `l-a pus pe task pe ${numele(e.data.newValue)}`
                                : Number(e.data.oldValue) === e.data.actor?.id
                                  ? 'a ieșit de pe task'
                                  : `l-a scos de pe task pe ${numele(e.data.oldValue)}`}
          {e.data.field !== 'asteapta' && faraMarcajArhivare(e.data.note) && (
            <span className="block text-mic italic text-b4l-text-muted">
              „{faraMarcajArhivare(e.data.note)}”
            </span>
          )}
        </Pas>
      ))}
    </ol>
  )
}

function Pas({ bulina, cand, children }: { bulina: string; cand: string; children: ReactNode }) {
  return (
    <li className="relative grid grid-cols-[14px_minmax(0,1fr)] gap-2.5 pb-3 before:absolute before:bottom-0 before:left-[5px] before:top-4 before:w-0.5 before:bg-b4l-border last:before:hidden">
      <span
        className={`mt-1 h-3 w-3 rounded-full ring-[3px] ring-b4l-surface-ridicata ${bulina}`}
        aria-hidden="true"
      />
      <div className="text-corp text-b4l-text [overflow-wrap:anywhere]">
        {children}
        <div className="text-mic text-b4l-text-dim">
          {ziScurt(cand)}, {oraScurt(cand)}
        </div>
      </div>
    </li>
  )
}

/**
 * Comentariu nou, cu @mentiuni. Serverul anunta DOAR oamenii trimisi in `mentionedIds` — un „@Ana"
 * scris de mana nu anunta pe nimeni. De aceea „@" deschide lista de colegi, iar cei alesi pleaca
 * odata cu textul.
 *
 * Lista are doar oamenii care pot vedea task-ul (aceeasi regula ca `vizibilitateTask` din API:
 * departamentul task-ului, departamentul care l-a cerut, oamenii de pe el, adminii; la un privat
 * DOAR oamenii de pe el si cine l-a dat). Altfel ai crede ca ai anuntat pe cineva care nu primeste
 * nimic.
 */
function ComentariuNou({
  taskId,
  departament,
  sursa,
  oameniPeTask,
  privat,
  euId,
  laTrimis,
}: {
  taskId: number
  departament: string | null
  sursa: string | null
  /** Responsabilii, plus cine l-a dat la un privat. */
  oameniPeTask: number[]
  privat: boolean
  euId: number | null
  laTrimis: () => void
}) {
  const [text, setText] = useState('')
  const [alesi, setAlesi] = useState<{ id: number; name: string }[]>([])
  const [cauta, setCauta] = useState<string | null>(null)
  const [activ, setActiv] = useState(0)
  const camp = useRef<HTMLTextAreaElement>(null)

  const oameni = useQuery<{ users: { id: number; name: string; role: string }[] }>({
    queryKey: ['taskuri', 'oameni'],
    queryFn: async () => (await api.get('/tasks/mentionable-users')).data,
    staleTime: 10 * 60_000,
  })
  const potVedea = (oameni.data?.users ?? []).filter((u) => {
    if (u.id === euId) return false
    if (oameniPeTask.includes(u.id)) return true
    if (privat) return false
    if (u.role === 'admin') return true
    const d = departamentulRolului(u.role)
    return d != null && (d === departament || d === sursa)
  })
  const sugestii =
    cauta == null ? [] : potVedea.filter((u) => fold(u.name).includes(fold(cauta))).slice(0, 6)

  const trimite = useMutation({
    mutationFn: async () => {
      const corp = text.trim()
      // Doar cei a caror mentiune a ramas in text — daca ai sters „@Ana", Ana nu mai e anuntata.
      const mentionedIds = alesi.filter((a) => corp.includes(`@${a.name}`)).map((a) => a.id)
      return (await api.post(`/tasks/${taskId}/comments`, { body: corp, mentionedIds })).data
    },
    onSuccess: () => {
      setText('')
      setAlesi([])
      laTrimis()
    },
    onError: (e) => toast.error(mesajEroare(e, 'Comentariul n-a plecat.')),
  })

  /** Cuvantul de dupa ultimul „@" dinaintea cursorului, daca omul tocmai scrie o mentiune. */
  const urmareste = (valoare: string, cursor: number) => {
    const inainte = valoare.slice(0, cursor)
    const m = /(^|\s)@([^\s@]{0,30})$/.exec(inainte)
    setCauta(m ? m[2] : null)
    setActiv(0)
  }

  const alege = (u: { id: number; name: string }) => {
    const el = camp.current
    const cursor = el?.selectionStart ?? text.length
    const inainte = text.slice(0, cursor).replace(/@[^\s@]{0,30}$/, `@${u.name} `)
    const nou = inainte + text.slice(cursor)
    setText(nou)
    setAlesi((a) => (a.some((x) => x.id === u.id) ? a : [...a, u]))
    setCauta(null)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(inainte.length, inainte.length)
    })
  }

  return (
    <form
      className="relative flex items-end gap-1.5"
      onSubmit={(e) => {
        e.preventDefault()
        if (text.trim() && !trimite.isPending) trimite.mutate()
      }}
    >
      <textarea
        ref={camp}
        rows={1}
        value={text}
        maxLength={5000}
        onChange={(e) => {
          setText(e.target.value)
          urmareste(e.target.value, e.target.selectionStart)
        }}
        onKeyDown={(e) => {
          if (sugestii.length > 0) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault()
              setActiv(
                (i) => (i + (e.key === 'ArrowDown' ? 1 : sugestii.length - 1)) % sugestii.length,
              )
              return
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault()
              alege(sugestii[activ])
              return
            }
          }
          if (e.key === 'Escape' && cauta != null) {
            e.stopPropagation()
            setCauta(null)
            return
          }
          // Enter trimite; Shift+Enter lasa rand nou.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (text.trim() && !trimite.isPending) trimite.mutate()
          }
        }}
        placeholder="Scrie… (@ ca să anunți pe cineva)"
        aria-label="Comentariu"
        aria-autocomplete="list"
        aria-expanded={sugestii.length > 0}
        className="max-h-40 min-h-[34px] min-w-0 flex-1 resize-y rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-corp text-b4l-text"
      />
      <Button type="submit" disabled={!text.trim()} loading={trimite.isPending}>
        Trimite
      </Button>
      {cauta != null && (
        <ul
          role="listbox"
          aria-label="Pe cine anunți"
          className="absolute bottom-full left-0 z-10 mb-1 grid w-64 rounded-caseta border border-b4l-border bg-b4l-surface-ridicata p-1 shadow-ridicat"
        >
          {sugestii.map((u, i) => (
            <li key={u.id} role="option" aria-selected={i === activ}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  alege(u)
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-corp text-b4l-text ${
                  i === activ ? 'bg-b4l-bg' : 'hover:bg-b4l-bg'
                }`}
              >
                <Avatar nume={u.name} />
                {u.name}
              </button>
            </li>
          ))}
          {sugestii.length === 0 && (
            <li className="px-2 py-1.5 text-mic text-b4l-text-dim">
              Niciun coleg care vede task-ul nu are numele ăsta.
            </li>
          )}
        </ul>
      )}
    </form>
  )
}

/** Fara diacritice si litere mari, ca „@stef" sa-l gaseasca pe „Ștefan". */
const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
